<template>
  <base-form
    :aria-labelledby="id"
    :class="brandClasses.form"
    :form
    :scroll-to-error="false"
    @submit="onSubmit"
  >
    <div v-if="showProductDetails">
      <div class="my-4 flex gap-2 lg:mt-2">
        <base-picture
          v-if="imageSrc"
          :alt="productTitle"
          class="shrink-0"
          :height="image.height"
          :src="getImageTransformations(imageSrc, image)"
          :width="image.width"
        />
        <div class="text-sm">
          <p v-if="productTitle" :class="storeProductTitle">
            {{ productTitle }}
          </p>
          <p v-for="attribute in selectedAttributes" :key="attribute?.label" :class="productAttributes">
            {{ attribute?.label }}: {{ attribute?.value }}
          </p>
        </div>
      </div>
      <base-collapse class-toggle="w-full my-4">
        <template #toggle="{ expanded }">
          <span class="flex items-center subtitle-1 between">
            {{ $t.howItWorks }}
            <vf-icon :dir="expanded ? 'up' : 'down'" name="plus" size="md" />
          </span>
        </template>
        <p :class="storeInstruction">
          {{ $t.tryBeforeBuy }}
        </p>
        <p class="mb-4 mt-2" :class="storeCopy">
          {{ $t.tryBeforeBuyTerms }}
        </p>
      </base-collapse>
    </div>
    <div class="b-t b-grey-70 pt-4">
      <h2 :id :class="brandClasses.heading">
        {{ $t.reservationTitle }}
      </h2>
      <p v-if="showRequiredFieldsDisclaimer" :class="brandClasses.requiredField">
        {{ $t.requiredField }}
      </p>
      <div :class="brandClasses.inputsWrapper">
        <vf-form-field
          v-slot="{ attrs }"
          name="firstName"
          :rule="[validateRequired(separateNameFields ? $t.firstName : $t.name), validateName()]"
        >
          <vf-input
            v-model="form.firstName"
            v-bind="attrs"
            :label="separateNameFields ? $t.firstName : $t.name"
            required
          />
        </vf-form-field>
        <vf-form-field
          v-slot="{ attrs }"
          v-if="separateNameFields"
          name="lastName"
          :rule="[validateRequired($t.lastName), validateName()]"
        >
          <vf-input v-model="form.lastName" v-bind="attrs" :label="$t.lastName" required />
        </vf-form-field>
        <vf-form-field v-slot="{ attrs }" name="email" :rule="[validateRequired($t.email), validateEmail()]">
          <input-email v-model="form.email" v-bind="attrs" />
        </vf-form-field>
        <vf-form-field
          v-slot="{ attrs }"
          name="phone"
          :rule="[validateRequired($t.mobileNumber), validatePhone(form.phoneCode)]"
        >
          <input-phone v-model="form.phone" v-model:code="form.phoneCode" v-bind="attrs" required />
        </vf-form-field>
      </div>
      <vf-form-field v-slot="{ attrs }" name="robisTerms" :rule="validateRequired('', $t.validators.requiredCheckbox)">
        <vf-checkbox v-model="form.robisTerms" v-bind="attrs" :size="checkboxSize">
          <form-legal-disclaimer :content="$t.reservationPolicy" />
        </vf-checkbox>
      </vf-form-field>
      <vf-button :class="brandClasses.cta" :loading type="submit">
        {{ enableReserveDialog ? $t.reserve : $t.submit }}
      </vf-button>
    </div>
  </base-form>
</template>

<script lang="ts" setup>
import type { StoreShippingAddress } from '#types/cart'
import type { ProductAttributeSelectionLabels } from '#types/product'

const { cartId, storeShippingAddress, variantId } = defineProps<{
  cartId: string
  storeShippingAddress: StoreShippingAddress
  imageSrc?: string
  productTitle?: string
  selectedAttributes?: ProductAttributeSelectionLabels
  variantId?: string
}>()

const emit = defineEmits<{
  success: []
}>()

const { cart } = useApi()
const {
  brandClasses,
  checkboxSize,
  separateNameFields,
  showProductDetails,
  showRequiredFieldsDisclaimer
} = useAppConfig().components.form.robis
const {
  brandClasses: {
    storeProductTitle,
    productAttributes,
    storeInstruction,
    storeCopy
  } = {},
  image
} = useAppConfig().components.bopis.storeDetails
const { enableReserveDialog } = useAppConfig().components.dialog.pickupInStore
const countryCode = useCountryCode()
const id = useId()
const { validateEmail, validateName, validateRequired } = useLocalizedValidators()
const { $t } = useNuxtApp()
const toast = useToaster()
const reservedVariantIds = useState('pdp-reserved-variant-ids', () => new Set<string>())

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  phoneCode: getPhoneCodeByCountryCode(countryCode) || '',
  robisTerms: false
})

const loading = ref(false)

const onSubmit = async () => {
  loading.value = true

  try {
    const address = {
      ...form,
      firstName: (separateNameFields ? form.firstName : form.firstName.split(' ')[0]) || '',
      lastName: (separateNameFields ? form.lastName : form.firstName.split(' ').slice(1).join(' ')) || '',
      phone: form.phone
        ? formatE164phone(
            form.phone,
            form.phoneCode
          )
        : '',
      city: storeShippingAddress.city,
      addressLine1: storeShippingAddress.addressLine1,
      postalCode: storeShippingAddress.postalCode,
      country: storeShippingAddress.country
    }

    await cart.$placeOrder({
      recaptcha_response: '',
      cartId,
      billingAddress: {
        ...storeShippingAddress,
        firstName: address.firstName,
        lastName: address.lastName,
        phone: address.phone,
        email: address.email,
        countryCode: address.country,
        subscriptions: {
          newsletterConsent: false
        }
      },
      paymentMethod: {
        code: 'PAY_IN_STORE',
        additionalData: []
      }
    }, { context: 'robis' })

    if (enableReserveDialog && variantId) reservedVariantIds.value.add(variantId)

    emit('success')

    closeDialogs()

    toast.add({
      autoClose: false,
      props: {
        type: 'success',
        title: $t.reservationSuccess.toastTitle,
        message: $t.reservationSuccess.toastMessage
      }
    })
  }
  catch (err) {
    log.error(`[robis] Failed to place reservation order: ${err}`)
    closeDialogs()

    toast.add({
      autoClose: false,
      props: {
        type: 'error',
        message: $t.reservationError
      }
    })
  }
  finally {
    loading.value = false
  }
}
</script>
